import { doc, query, where, addDoc, getDoc, getDocs, updateDoc, collection, serverTimestamp } from "firebase/firestore";
import { db } from "../config/FirebaseConfig"; // db exported from your config

class SubscriptionApi {
    // '<' operator is used to filter based on less than
    // '<=' operator is used to filter based on less than or equal to
    // '==' operator is used to filter based on equal to
    // '>' operator is used to filter based on greater than
    // '>=' operator is used to filter based on greater than or equal to
    // '!=' operator is used to filter based on not equal to
    // 'array-contains' operator is used to filter based on array contains
    // 'array-contains-any' operator is used to filter based on array contains any
    // 'in' operator is used to filter based on in
    // 'not-in' operator is used to filter based on not in

    /*
    {
        userId: userId,                // Link to producer
        companyId: companyId | null,   // If company-wide subscription
        planType: "basic" | "pro" | ...,
        startDate: timestamp,
        renewedDate: timestamp,
        endDate: timestamp,
        isActive: boolean,
        isDeleted: boolean,
        createdAt: timestamp,
        updatedAt: timestamp
        }
    */

    async getSubscription(id = null) {
        if (!id) {
            console.error("Invalid subscription ID:", id);
            return { success: false, error: "Invalid subscription ID" };
        }
        try {
            const subscriptionDoc = await getDoc(doc(db, "subscriptions", id));
            if (subscriptionDoc.exists()) {
                return { success: true, subscription: subscriptionDoc.data() };
            } else {
                console.error("Subscription not found:", id);
                return { success: false, error: "Subscription not found" };
            }
        } catch (error) {
            console.error("Error fetching subscription:", error);
            return { success: false, error: error.message };
        }
    }

    async getSubscriptionsByUserId(userId = null) {
        if (!userId) {
            console.error("Invalid user ID:", userId);
            return { success: false, error: "Invalid user ID" };
        }
        try {
            const q = query(collection(db, "subscriptions"), where("userId", "==", userId));
            const querySnapshot = await getDocs(q);
            const subscriptions = [];
            querySnapshot.forEach((doc) => {
                subscriptions.push({ id: doc.id, ...doc.data() });
            });
            return { success: true, subscriptions };
        } catch (error) {
            console.error("Error fetching subscriptions by user ID:", error);
            return { success: false, error: error.message };
        }
    } 

    async getSubscriptionsByCompanyId(companyId = null) {
        if (!companyId) {
            console.error("Invalid company ID:", companyId);
            return { success: false, error: "Invalid company ID" };
        }
        try {
            const q = query(collection(db, "subscriptions"), where("companyId", "==", companyId));
            const querySnapshot = await getDocs(q);
            const subscriptions = [];
            querySnapshot.forEach((doc) => {
                subscriptions.push({ id: doc.id, ...doc.data() });
            });
            return { success: true, subscriptions };
        } catch (error) {
            console.error("Error fetching subscriptions by company ID:", error);
            return { success: false, error: error.message };
        }
    }

    async createSubscription(body = {}) {
        if (!body || !body.userId || !body.planType) {
            console.error("Invalid subscription data:", body);
            return { success: false, error: "Invalid subscription data" };
        }
        try {
            const subscriptionRef = await addDoc(collection(db, "subscriptions"), {
                userId: body.userId,
                companyId: body.companyId || null,
                planType: body.planType || "basic",
                startDate: serverTimestamp(),
                renewedDate: serverTimestamp(),
                endDate: body.endDate || new Date(Date.now() + 31 * 24 * 60 * 60 * 1000), // Default to 31 days from now
                isActive: true,
                isDeleted: false,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
            });

            return { success: true, subscriptionId: subscriptionRef.id }; // Return the ownerId as companyId for reference
        } catch (error) {
            console.error("Error creating subscription:", error);
            return { success: false, error: error.message };
        }
    }

    async updateSubscription(id = null, body = {}) {
        if (!id || !body) {
            console.error("Invalid subscription ID or data:", id, body);
            return { success: false, error: "Invalid subscription ID or data" };
        }
        try {
            const subscriptionRef = doc(db, "subscriptions", id);
            await updateDoc(subscriptionRef, {
                ...body,
                updatedAt: serverTimestamp(),
            });
            return { success: true };
        } catch (error) {
            console.error("Error updating subscription:", error);
            return { success: false, error: error.message };
        }
    }
}

export default SubscriptionApi;